import { Accordion, AccordionItem } from "@carbon/react";
import { FC } from "react";

import EmptyTableState from "src/components/DataStates/EmptyTableState.tsx";
import WidgetContainer, {
  WidgetContainerProps,
} from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";

const GRID_COLS_CLASS: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
};

const DEFAULT_COLUMNS_COUNT = 3;
const EMPTY_VALUE_LABEL = "—";

export type ElementListWidgetPropertyValue = {
  property: { code: string; name: string };
  value?: string | null;
};

export type ElementListWidgetItem = {
  id: string;
  name: string;
  properties: ElementListWidgetPropertyValue[];
};

export type ElementListWidgetData = {
  code: string;
  name: string;
  typeCode: string;
  elements: ElementListWidgetItem[];
};

export type ElementListWidgetConfiguration = {
  elementListCode?: string;
  columns?: number;
};

export type ElementListWidgetProps = {
  data?: ElementListWidgetData;
  configuration?: ElementListWidgetConfiguration;
} & Omit<WidgetContainerProps, "children">;

const getGridColsClass = (columns?: number) => {
  if (!columns) return GRID_COLS_CLASS[DEFAULT_COLUMNS_COUNT];
  return GRID_COLS_CLASS[columns] ?? GRID_COLS_CLASS[DEFAULT_COLUMNS_COUNT];
};

const getDisplayValue = (value?: string | null) =>
  value && value.trim().length > 0 ? value : EMPTY_VALUE_LABEL;

export const ElementListWidget: FC<ElementListWidgetProps> = ({
  data,
  configuration,
  ...containerProps
}) => {
  const gridColsClass = getGridColsClass(configuration?.columns);

  const elements = data?.elements ?? [];

  return (
    <WidgetContainer {...containerProps}>
      {elements.length === 0 ? (
        <EmptyTableState />
      ) : (
        <Accordion align="start" size="sm">
          {elements.map((element, index) => (
            <AccordionItem title={element.name} key={element.id} open={index === 0}>
              <div
                className={`grid ${gridColsClass} gap-5 py-3`}
                data-testid={`element-list-properties-grid-${element.id}`}
              >
                {element.properties.map((propertyValue) => (
                  <div key={propertyValue.property.code} className="flex flex-col gap-2">
                    <p className="text-sm text-textSecondary">{propertyValue.property.name}</p>
                    <p className="text-sm">{getDisplayValue(propertyValue.value)}</p>
                  </div>
                ))}
              </div>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </WidgetContainer>
  );
};

export default ElementListWidget;
